<template>
    <div class="distribution_card">
        <div class="card_header is-flex is-align-items-center">
            <PieChartIcon class="header_icon" />
            <span class="card_title">{{ title }}</span>
        </div>

        <div v-if="hasDistributions" class="distributions_grid">
            <div v-for="dist in distributions" :key="dist.category_id"
                class="distribution_row">
                <div class="col_label">{{ dist.category_label }}</div>
                <div class="distribution_body">
                    <div class="donut_container" :style="donutStyle">
                        <EChart v-if="chartReady" :option="chartOption(dist)"
                            @chart-mouseover="onChartHover(dist, $event)"
                            @chart-mouseout="onChartLeave" />
                    </div>
                    <ul class="ranking_list">
                        <li v-for="(item, idx) in dist.items" :key="item.name"
                            class="ranking_item"
                            :class="{ 'is-focused': isFocused(dist, item.name) }"
                            @mouseenter="onItemEnter(dist, item.name)"
                            @mouseleave="onItemLeave">
                            <span class="ranking_label">{{ item.name }}</span>
                            <div class="ranking_bar_track">
                                <div class="ranking_bar_fill"
                                    :style="{
                                        width: barWidth(item.share),
                                        background: palette(dist)[idx],
                                    }"></div>
                            </div>
                            <span class="ranking_meta">
                                <span class="ranking_amount">{{ formatMoney(item.amount) }}</span>
                                <span class="ranking_dot" aria-hidden="true">·</span>
                                <span class="ranking_share">{{ formatShare(item.share) }}</span>
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <p v-else class="empty_state">
            Sin categorías clasificadas para mostrar distribución.
        </p>
    </div>
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";
import { parseToMoney } from "@/utils/currency";
import { interpolateHexPalette } from "@/utils/colorPalette";

// Anchor tokens — leídos por getComputedStyle en mounted porque el canvas
// de ECharts no resuelve var(...). Mantener este par como los extremos del
// gradiente del que se interpolan N colores; el theme define el rango.
const LIGHTEST_ANCHOR = "--report-chart-segment-3";
const DARKEST_ANCHOR = "--report-chart-segment-1";

// Tamaño fijo del donut (px). Única fuente de verdad para dimensionar el
// contenedor inline (el EChart wrapper difiere su render hasta que el host
// mide > 0, así que no hace falta pasarle init-options explícitas).
const DONUT_PX = 132;

export default {
    name: "StoresDistributionCard",
    components: { EChart, PieChartIcon },
    props: {
        // Lista de distribuciones — una entrada por categoría configurada.
        // Backend devuelve hasta 2. Cada entry: {category_id, category_label,
        // items: [{name, amount, share}]}.
        distributions: {
            type: Array,
            required: true,
            validator: (arr) => arr.every(
                (d) => typeof d.category_id === "string" && Array.isArray(d.items),
            ),
        },
    },
    data() {
        // chartReady arranca false porque necesitamos resolver los anchor
        // tokens vía getComputedStyle antes del primer render. Evita el flash
        // del donut sin paleta y el caso "los anchors no existen" (theme mal
        // configurado): si el palette queda vacío, no rendereamos el chart.
        // `focusedCategory`: cuando un item está hovereado (chart o lista),
        // emitimos arriba qué categoría + valor, para que el ranking de
        // clientes resalte las contrapartes asociadas a ese atributo.
        return {
            chartReady: false,
            lightAnchor: "",
            darkAnchor: "",
            focusedCategory: null, // { category_id, value } | null
        };
    },
    computed: {
        title() {
            // Card apila varias distribuciones (cuenta + categorías), cada una
            // con su propio label de fila — el título de la card es genérico.
            return "Distribución de ventas";
        },
        hasDistributions() {
            return this.distributions.some((d) => d.items.length > 0);
        },
        donutStyle() {
            return { width: `${DONUT_PX}px`, height: `${DONUT_PX}px` };
        },
    },
    mounted() {
        this.resolveCssColors();
        this.chartReady = !!this.lightAnchor && !!this.darkAnchor;
    },
    methods: {
        resolveCssColors() {
            if (typeof window === "undefined") return;
            const root = getComputedStyle(document.documentElement);
            this.lightAnchor = root.getPropertyValue(LIGHTEST_ANCHOR).trim();
            this.darkAnchor = root.getPropertyValue(DARKEST_ANCHOR).trim();
        },
        palette(dist) {
            // N colores interpolados entre dark y light — el item con más
            // share va más claro, los menores más oscuros (matchea el donut).
            if (!this.lightAnchor || !this.darkAnchor) return [];
            return interpolateHexPalette(
                this.lightAnchor, this.darkAnchor, dist.items.length,
            );
        },
        chartOption(dist) {
            const palette = this.palette(dist);
            return {
                tooltip: {
                    trigger: "item",
                    // El donut es chico y vive en una card con overflow:hidden
                    // (y dentro del modal). Sin esto el tooltip se recorta:
                    // appendToBody lo monta como hijo de <body> para que escape
                    // el clipping, y confine:false lo deja posicionarse libre.
                    appendToBody: true,
                    confine: false,
                    formatter: (params) => {
                        const share = Math.round(params.percent ?? 0);
                        return `<div class="echart_tooltip">
                            <strong>${params.name}</strong><br>
                            ${parseToMoney(params.value)} · ${share}%
                        </div>`;
                    },
                },
                series: [
                    {
                        type: "pie",
                        radius: ["62%", "92%"],
                        padAngle: 3,
                        avoidLabelOverlap: false,
                        label: { show: false },
                        labelLine: { show: false },
                        itemStyle: { borderRadius: 3 },
                        data: dist.items.map((s, i) => ({
                            value: s.amount,
                            name: s.name,
                            itemStyle: { color: palette[i] },
                        })),
                    },
                ],
            };
        },
        // Hover handlers — chart (mouseover sobre slice) y lista (mouseenter
        // sobre item) convergen al mismo state, así el highlight es
        // consistente. mouseleave en cualquiera limpia.
        onChartHover(dist, params) {
            // params.name = label del slice hovereado.
            this.setFocus(dist.category_id, params.name);
        },
        onChartLeave() {
            this.clearFocus();
        },
        onItemEnter(dist, value) {
            this.setFocus(dist.category_id, value);
        },
        onItemLeave() {
            this.clearFocus();
        },
        setFocus(categoryId, value) {
            this.focusedCategory = { category_id: categoryId, value };
            this.$emit("focus-category", this.focusedCategory);
        },
        clearFocus() {
            this.focusedCategory = null;
            this.$emit("blur-category");
        },
        isFocused(dist, value) {
            return this.focusedCategory
                && this.focusedCategory.category_id === dist.category_id
                && this.focusedCategory.value === value;
        },
        sharePercent(share) {
            const v = Math.max(0, Math.min(1, share ?? 0));
            return Math.round(v * 100);
        },
        barWidth(share) {
            return `${this.sharePercent(share)}%`;
        },
        formatMoney(v) {
            return parseToMoney(v);
        },
        formatShare(share) {
            return `${this.sharePercent(share)}%`;
        },
    },
};
</script>

<style scoped lang="scss">
.distribution_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: var(--report-shadow-card);
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.card_header {
    margin-bottom: 12px;
    color: var(--report-text);
    flex-shrink: 0;

    .header_icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card_title {
        font-size: 14px;
        font-weight: 600;
    }
}

// Stack vertical de distribuciones (cuenta + categorías). El scroll vive en
// el contenedor: cada fila se autodimensiona y la card scrollea si la suma
// excede el alto disponible.
.distributions_grid {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

// Cada distribución: label arriba (full width) + body horizontal
// (donut izquierda, detalle derecha). Separador suave entre filas.
.distribution_row {
    display: flex;
    flex-direction: column;
    min-width: 0;

    & + & {
        padding-top: 20px;
        border-top: 1px solid var(--report-divider-soft);
    }
}

.col_label {
    font-size: 12px;
    font-weight: 600;
    color: var(--report-text-muted-strong);
    text-transform: capitalize;
    margin-bottom: 8px;
    flex-shrink: 0;
}

// Donut a la izquierda, lista de detalle a la derecha.
.distribution_body {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
}

// Donut cuadrado a la izquierda — no se comprime. El tamaño (width/height) lo
// pinta inline desde DONUT_PX (única fuente de verdad, compartida con las
// init-options de ECharts); acá sólo el comportamiento de layout.
.donut_container {
    flex-shrink: 0;
}

.ranking_list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-width: 0;
}

.ranking_item {
    display: grid;
    // Nombre acotado para que la barra (1fr) tome el mayor ancho posible —
    // la barra es la lectura comparativa, el nombre trunca con ellipsis.
    grid-template-columns: minmax(72px, 116px) 1fr auto;
    gap: 10px;
    align-items: center;
    padding: 5px 6px;
    font-size: 13px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 120ms ease;

    &:hover,
    &.is-focused {
        background: var(--report-surface);
    }
}

.ranking_label {
    color: var(--report-text);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ranking_bar_track {
    background: var(--report-progress-bg);
    border-radius: 999px;
    height: 10px;
    overflow: hidden;
}

.ranking_bar_fill {
    height: 100%;
    border-radius: 999px;
    transition: width 200ms ease-out;
}

.ranking_meta {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    color: var(--report-text-muted);
    font-size: 12px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    .ranking_amount {
        color: var(--report-text-muted-strong);
        font-weight: 500;
    }

    .ranking_dot {
        color: var(--report-text-muted-soft);
        font-weight: 700;
    }

    .ranking_share {
        color: var(--report-text-muted);
        font-weight: 500;
    }
}

.empty_state {
    color: var(--report-text-muted);
    font-size: 13px;
    text-align: center;
    padding: 24px 8px;
    margin: auto;
}
</style>
